import * as React from 'react';
import { message } from 'antd';
import { useSetAtom, useAtom, useAtomValue } from 'jotai';
import { useDispatch } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import { CURRENT_PRODUCT_MODE, PRODUCT_MODE_MAP } from '@utils/constants';
import { getValidFilterDimensionMeta, isValidSavedFilter } from '@utils';
import { getOrderedRulesWithName } from '@utils/filter';
import apptracker from '@utils/sensor/apptracker';
import * as EVENTS from '@src/utils/sensor/track';
import { GlobalFilterRefAtom } from '@src/atoms/page';
import { primaryFilterVisibilityAtom } from '@src/components/metrics-panel-hz/atoms';
import { FIXED_FILTERS, FIXED_APP_FILTERS } from '@src/components/filter-panel/constants';
import * as apis from '@src/api/filters';
import FilterDialog, { type FilterDialogProps, type FilterDialogRef } from '../components/Dialog';
import { getDataSource } from '../components/utils';
import {
  enableFilterAtom,
  globalFilterRulesExpandedAtom,
  globalFilterAppendedAtomStatusAtom,
} from '../global-atoms';
import { useCreateFilter, useFilterMeta } from './hooks';

// Give a negative number to preset filters'`fid`, so that they can be distinguished from saved filters
const PRESET_FILTERS = (CURRENT_PRODUCT_MODE === 'APP' ? FIXED_APP_FILTERS : FIXED_FILTERS).map(
  (it, ix) => ({ ...it, fid: -1000 - ix }),
);

const filterDataSource = getDataSource();
const filterMetaEnabled = [PRODUCT_MODE_MAP.EI, PRODUCT_MODE_MAP.AI, PRODUCT_MODE_MAP.APP].includes(
  CURRENT_PRODUCT_MODE,
);

const useCheckSavedFilterInValid = (dimensionsList: any[]) => {
  return React.useCallback(
    (filter) => !isValidSavedFilter(filter, dimensionsList),
    [dimensionsList],
  );
};

const DynamicFilterBuilder = React.memo(
  ({ onApplyFilter, appliedFilter, dimensionsList, dimensionsLookUp }: any) => {
    const filterDialogRef = React.useRef<FilterDialogRef>(null);
    const storeDispatch = useDispatch();
    const checkSavedFilterInValid = useCheckSavedFilterInValid(dimensionsList);
    const filterMeta = useFilterMeta(filterDataSource, {
      enabled: filterMetaEnabled,
      select: (data) => ({
        ...data,
        dimensions: data.dimensions.toSorted(({ name: a }, { name: b }) =>
          a === b ? 0 : a > b ? 1 : -1,
        ),
      }),
    });
    const enableFilter = useAtomValue(enableFilterAtom);

    const setGlobalFilterRef = useSetAtom(GlobalFilterRefAtom);
    const [showPrimaryFilterSeries, setShowPrimaryFilterSeries] = useAtom(
      primaryFilterVisibilityAtom,
    );
    const [rulesExpanded, setRulesExpanded] = useAtom(globalFilterRulesExpandedAtom);
    const [appendedAtomStatus, setAppendedAtomStatus] = useAtom(globalFilterAppendedAtomStatusAtom);
    const filterMetadata = React.useMemo(() => {
      if (!filterMeta.data) return filterMeta.data;

      const dimensions = filterMeta.data.dimensions || [];
      const validDimensions = getValidFilterDimensionMeta(dimensions, dimensionsList);

      return {
        ...filterMeta.data,
        dimensions: validDimensions,
      };
    }, [filterMeta.data, dimensionsList]);
    const { mutateAsync: filterSaveMutate } = useCreateFilter();

    const customTagMeta = filterMetadata?.dimensions.find((it) => it.name === 'Tag')?.fields[0];
    const customTagSearchUrl = customTagMeta?.urls.key;

    // Callbacks
    const handleChange = React.useCallback<NonNullable<FilterDialogProps['onChange']>>(
      (value) => {
        const { fid, name, rule } = value;
        const isSavedFilter = typeof fid === 'number' && fid > 0;
        // Remove the negative number `fid` to keep the compatibility with old filter dialog
        const realFilterId = isSavedFilter ? fid : undefined;

        // saved filter including event dimension can't be selected under Session data scope
        if (isSavedFilter && checkSavedFilterInValid(value as any)) {
          message.warning('Only Global Filters can be applied.');
          return;
        }

        const orderedRules = getOrderedRulesWithName(cloneDeep(rule));

        const filterValue = {
          rule: orderedRules.rule,
          name: name || (orderedRules.rule.length ? orderedRules.name : undefined),
          fid: realFilterId,
        };
        onApplyFilter(filterValue);
      },
      [storeDispatch, checkSavedFilterInValid],
    );
    const handleRecentFilterSearch = React.useCallback(async () => {
      const res = await apis.getFiltersBySearch({
        params: {
          sortBy: 'createdAt',
          sortOrder: 'desc',
          limit: 20,
        },
        ...apis.getV2AxiosConfig(),
      });
      return res.filters;
    }, []);
    const handleSavedFilterSearch = React.useCallback(async (keyword?: string) => {
      apptracker(EVENTS.FILTER_BUILDER_SAVED_FILTER_SEARCH, { keyword });
      const res = await apis.getFiltersBySearch({
        params: {
          searchText: keyword,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          limit: 200,
        },
        ...apis.getV2AxiosConfig(),
      });
      return res.filters;
    }, []);
    const handleCustomTagSearch = React.useCallback(
      async (keyword?: string) => {
        if (customTagSearchUrl && keyword) {
          const res = await apis.getAutoCompletion<{ text: string; value: string }[]>(
            customTagSearchUrl,
            keyword,
          );
          return res.data;
        }

        return [];
      },
      [customTagSearchUrl],
    );
    const handleDimensionValueSearch = React.useCallback((keyword?: string, apiUrl?: string) => {
      if (!apiUrl || !keyword) return [];

      return apis
        .getAutoCompletion<{ text: string; value: string }[]>(apiUrl, keyword)
        .then((res) => res.data);
    }, []);
    const handleFilterSave = React.useCallback<NonNullable<FilterDialogProps['onFilterSave']>>(
      async (filter) => {
        const res = await filterSaveMutate({
          filter,
          dataSource: filterDataSource,
        });

        return res.data.filter;
      },
      [filterSaveMutate],
    );

    React.useEffect(() => {
      setGlobalFilterRef(filterDialogRef);

      // Clean up, remove the reference to avoid memory leak.
      return () => {
        setGlobalFilterRef(undefined);
      };
    }, [setGlobalFilterRef]);

    if (filterMetaEnabled && filterMeta.isLoading) return null;

    return (
      <div
        data-testid="filter-dialog"
        style={{ position: 'relative', zIndex: 50, wordBreak: 'initial', maxWidth: '100%' }}
      >
        <FilterDialog
          ref={filterDialogRef}
          defaultRulesExpanded={rulesExpanded}
          defaultAppendedAtomStatus={appendedAtomStatus}
          disabled={!enableFilter || !filterMetaEnabled}
          compareMode={false}
          compareSeries={showPrimaryFilterSeries}
          meta={filterMetadata}
          dimensionLookup={dimensionsLookUp}
          presetFilters={PRESET_FILTERS}
          value={appliedFilter}
          onChange={handleChange}
          onCompareSeriesChange={setShowPrimaryFilterSeries}
          onRulesExpandChange={setRulesExpanded}
          onAppendedAtomStatusChange={setAppendedAtomStatus}
          onRecentFilterSearch={handleRecentFilterSearch}
          onSavedFilterSearch={handleSavedFilterSearch}
          onFilterSave={handleFilterSave}
          onCustomTagSearch={customTagSearchUrl ? handleCustomTagSearch : undefined}
          onDimensionValueSearch={handleDimensionValueSearch}
        />
      </div>
    );
  },
);

DynamicFilterBuilder.displayName = 'DynamicFilterBuilder';

export default DynamicFilterBuilder;
